import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import {
  articleAdminApi,
  ArticlePlanItem,
  ArticlePlansResult,
} from '@/features/admin/articleAdminApi';

const DEFAULT_LIMIT = 20;
const getTodayDate = (): string => new Date().toISOString().slice(0, 10);

export const ArticlePlan: React.FC = () => {
  const navigate = useNavigate();
  const [createdDate, setCreatedDate] = useState<string>(getTodayDate());
  const [createdBy, setCreatedBy] = useState<string>('');
  const [plans, setPlans] = useState<ArticlePlanItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = async (
    filters: { createdDate: string; createdBy: string },
    options?: { cursor?: string; append?: boolean }
  ): Promise<void> => {
    const normalizedCreatedDate = filters.createdDate.trim();
    const normalizedCreatedBy = filters.createdBy.trim();
    const isAppend = options?.append === true;

    if (!normalizedCreatedDate && !normalizedCreatedBy) {
      setError('Created date or editor is required.');
      if (!isAppend) {
        setPlans([]);
        setCursor(undefined);
      }
      return;
    }

    if (isAppend) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response: ArticlePlansResult = await articleAdminApi.getPlans({
        createdDate: normalizedCreatedDate || undefined,
        createdBy: normalizedCreatedBy || undefined,
        limit: DEFAULT_LIMIT,
        cursor: options?.cursor,
        order: 'desc',
      });

      setPlans(currentPlans => (isAppend ? [...currentPlans, ...response.data] : response.data));
      setCursor(response.cursor);
    } catch (err) {
      const message =
        err instanceof AxiosError
          ? ((err.response?.data as { message?: string } | undefined)?.message ?? err.message)
          : err instanceof Error
            ? err.message
            : 'Failed to load article plans';
      setError(message);
      if (!isAppend) {
        setPlans([]);
        setCursor(undefined);
      }
    } finally {
      if (isAppend) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void fetchPlans({ createdDate: getTodayDate(), createdBy: '' });
  }, []);

  const handleSearch = async (): Promise<void> => {
    await fetchPlans({ createdDate, createdBy });
  };

  const handleReset = async (): Promise<void> => {
    const today = getTodayDate();
    setCreatedDate(today);
    setCreatedBy('');
    await fetchPlans({ createdDate: today, createdBy: '' });
  };

  const formatDateTime = (value?: string): string => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const renderScope = (plan: ArticlePlanItem): string => {
    if (plan.groupedBy === 'topic') {
      return plan.topicCode || '-';
    }
    return plan.countryCode || '-';
  };

  const getArticleTypeLabel = (type?: string): string => {
    switch (type) {
      case 'monologue':
        return 'M';
      case 'dialogue':
        return 'D';
      default:
        return '-';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex justify-between items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Article Plan</h1>
          <p className="text-gray-600 mt-2">Review created article plans by date and by editor.</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/editor/articles')}>
          ← Back to Manage Articles
        </Button>
      </div>

      <Card className="p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label htmlFor="createdDate" className="block text-sm font-medium text-gray-700 mb-1">
              Created Date
            </label>
            <input
              id="createdDate"
              type="date"
              value={createdDate}
              onChange={event => setCreatedDate(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="createdBy" className="block text-sm font-medium text-gray-700 mb-1">
              Editor
            </label>
            <input
              id="createdBy"
              type="text"
              value={createdBy}
              onChange={event => setCreatedBy(event.target.value)}
              placeholder="Enter editor name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => void handleSearch()} disabled={loading}>
              Search
            </Button>
            <Button variant="outline" onClick={() => void handleReset()} disabled={loading}>
              Reset
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <Card className="p-4 mb-6 bg-red-50 border border-red-200">
          <p className="text-red-700">{error}</p>
        </Card>
      )}

      {loading ? (
        <Card className="p-6">
          <p className="text-gray-600">Loading article plans...</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Group
                  </th> */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scope
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Article Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prompted Topic
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prompted Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Editor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Article ID
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plans.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-6 text-center text-gray-500">
                      No article plans found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  plans.map(plan => (
                    <tr key={`${plan.articleId}-${plan.createdAt}`} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {formatDateTime(plan.createdAt)}
                      </td>
                      {/* <td className="px-4 py-4 text-sm text-gray-700 capitalize whitespace-nowrap">
                        {plan.groupedBy || '-'}
                      </td> */}
                      <td className="px-4 py-4 text-sm text-gray-700">{renderScope(plan)}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {plan.categoryCode || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {getArticleTypeLabel(plan.articleType)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {plan.tags && plan.tags.length > 0 ? plan.tags.join(', ') : '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 min-w-[150px]">
                        {plan.title || plan.titleUS || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 min-w-[150px]">
                        {plan.articleTopic || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 min-w-[300px]">
                        {plan.description || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 ">{plan.createdBy || '-'}</td>
                      <td className="px-4 py-4 text-xs text-gray-500 font-mono">
                        {plan.articleId}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {cursor && !loading && (
        <div className="mt-6 flex justify-center">
          <Button
            variant="outline"
            onClick={() => void fetchPlans({ createdDate, createdBy }, { cursor, append: true })}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
};
